/**
 * SkillIcon — "holographic" gradient tile for skills & categories.
 *
 * Renders a lucide line-icon (by name, stored on the skill/category) over a
 * soft per-category gradient, color-keyed by accent. Falls back to the emoji,
 * then a default sparkle, when no icon name is set.
 */
import {
  Mic, Megaphone, Target, Send, Mail, Ruler, PenTool, Code, HeartHandshake,
  BarChart3, LayoutGrid, Compass, Sparkles, type LucideIcon,
} from 'lucide-react';

// DB icon name (kebab) → lucide component.
const ICON_MAP: Record<string, LucideIcon> = {
  mic: Mic,
  megaphone: Megaphone,
  target: Target,
  send: Send,
  mail: Mail,
  ruler: Ruler,
  'pen-tool': PenTool,
  code: Code,
  'heart-handshake': HeartHandshake,
  'bar-chart-3': BarChart3,
  'layout-grid': LayoutGrid,
  compass: Compass,
  sparkles: Sparkles,
};

// accent → gradient + ring + icon color (literal classes so Tailwind keeps them).
const ACCENT: Record<string, { grad: string; ring: string; text: string }> = {
  indigo:  { grad: 'from-indigo-100 via-white to-sky-100',     ring: 'ring-indigo-200/70',  text: 'text-indigo-600' },
  emerald: { grad: 'from-emerald-100 via-white to-teal-100',   ring: 'ring-emerald-200/70', text: 'text-emerald-600' },
  amber:   { grad: 'from-amber-100 via-white to-rose-100',     ring: 'ring-amber-200/70',   text: 'text-amber-600' },
  sky:     { grad: 'from-sky-100 via-white to-cyan-100',       ring: 'ring-sky-200/70',     text: 'text-sky-600' },
  rose:    { grad: 'from-rose-100 via-white to-pink-100',      ring: 'ring-rose-200/70',    text: 'text-rose-600' },
  violet:  { grad: 'from-violet-100 via-white to-fuchsia-100', ring: 'ring-violet-200/70',  text: 'text-violet-600' },
  cyan:    { grad: 'from-cyan-100 via-white to-blue-100',      ring: 'ring-cyan-200/70',    text: 'text-cyan-600' },
  orange:  { grad: 'from-orange-100 via-white to-amber-100',   ring: 'ring-orange-200/70',  text: 'text-orange-600' },
};

interface SkillIconProps {
  icon?: string | null;
  emoji?: string | null;
  accent?: string | null;
  /** Tile size in px (icon scales with it). */
  size?: number;
  className?: string;
}

export function SkillIcon({ icon, emoji, accent, size = 44, className = '' }: SkillIconProps) {
  const a = ACCENT[accent || 'indigo'] || ACCENT.indigo;
  const Lucide = icon ? ICON_MAP[icon] : undefined;
  const iconPx = Math.round(size * 0.46);
  const radius = size >= 56 ? '1rem' : '0.75rem';

  return (
    <div
      className={`relative flex flex-shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br ${a.grad} ring-1 ${a.ring} shadow-sm ${className}`}
      style={{ width: size, height: size, borderRadius: radius }}
    >
      {/* holographic sheen */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-white/50 via-transparent to-white/10" />
      {Lucide ? (
        <Lucide style={{ width: iconPx, height: iconPx }} className={`relative ${a.text}`} strokeWidth={1.75} />
      ) : (
        <span className="relative" style={{ fontSize: iconPx, lineHeight: 1 }}>{emoji || '✨'}</span>
      )}
    </div>
  );
}

export default SkillIcon;
